import classNames from 'classnames';
import React, { useRef, useEffect } from 'react';
import { useSliderMomentum } from 'hooks';
import useIsMobile from 'hooks/utils/useIsMobile';
import styles from './slider.module.scss';
import Icon from '../icon';

const Slider = ({
  title,
  titleClassName,
  subtitleContent,
  children,
  slidesPerView,
  contentId,
  contentRole,
}) => {
  const carouselRef = useRef(null);
  const isMobile = useIsMobile();

  const { mobile: slidesPerViewMobile = 1.25, desktop: slidesPerViewDesktop = 5 } =
    slidesPerView || {};

  const hasArrows =
    (isMobile && children?.length > slidesPerViewMobile) ||
    (!isMobile && children?.length > slidesPerViewDesktop);

  useSliderMomentum(carouselRef);

  useEffect(() => {
    // scroll carousel to the begining on children change
    if (carouselRef.current) {
      carouselRef.current.scrollTo({
        left: 0,
        behavior: 'smooth',
      });
    }
  }, [children]);

  const handleArrowClick = (e, direction) => {
    e.preventDefault();
    const carousel = carouselRef.current;
    if (!carousel) return;

    const slides = carousel.querySelectorAll(`.${styles.slidesContainer} > *`);
    if (!slides.length) return;

    const slidesPerViewValue = Math.floor(
      isMobile ? slidesPerView?.mobile || 1.25 : slidesPerView?.desktop || 5
    );

    // Get current scroll position
    const currentScrollLeft = carousel.scrollLeft;

    // Calculate the gap size between slides
    // Getting computed styles for container to get gap and padding
    const containerStyle = window.getComputedStyle(
      carousel.querySelector(`.${styles.slidesContainer}`)
    );
    const gap = parseFloat(containerStyle.gap) || parseFloat(containerStyle.columnGap) || 0;

    const visibleSlides = [];

    for (let i = 0; i < slides.length; i++) {
      const slide = slides[i];
      const slideLeft = slide.offsetLeft;
      const slideRight = slideLeft + slide.offsetWidth;

      const isFullVisible =
        slideLeft >= currentScrollLeft && slideRight <= currentScrollLeft + carousel.clientWidth;
      const isPartiallyVisible =
        (slideLeft < currentScrollLeft && slideRight > currentScrollLeft) ||
        (slideRight > currentScrollLeft + carousel.clientWidth &&
          slideLeft < currentScrollLeft + carousel.clientWidth);

      // Check if slide is visible (at least partially)
      if (isFullVisible || isPartiallyVisible) {
        visibleSlides.push({ index: i, visibility: isFullVisible ? 'full' : 'partial' });
      }
    }

    let targetIndex;

    if (direction === 'right') {
      const lastVisibleIndex = visibleSlides[visibleSlides.length - 1];
      if (lastVisibleIndex.visibility === 'full') targetIndex = lastVisibleIndex.index + 1;
      // Scroll to the next element
      else targetIndex = lastVisibleIndex.index; // Keep partially visible element in view

      if (targetIndex >= slides.length) {
        targetIndex = slides.length - 1; // Prevent going out of bounds
      }
    } else {
      const firstVisibleIndex = visibleSlides[0];
      if (firstVisibleIndex.visibility === 'full')
        targetIndex = firstVisibleIndex.index - slidesPerViewValue;
      // Scroll to the previous element
      else targetIndex = firstVisibleIndex.index - slidesPerViewValue + 1; // Keep partially visible element in view

      if (targetIndex < 0) {
        targetIndex = 0; // Prevent going out of bounds
      }
    }

    // Get the target element and scroll to it
    if (targetIndex >= 0 && targetIndex < slides.length) {
      const targetElement = slides[targetIndex];

      // Calculate position to scroll to
      // For right scroll: align target to left of viewport
      // For left scroll: align target element to left of viewport
      carousel.scrollTo({
        left: targetElement.offsetLeft - gap,
        behavior: 'smooth',
      });
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.sliderHead}>
        <h2 className={classNames(styles.title, titleClassName)}>{title}</h2>
        {subtitleContent}
        <span className={styles.arrowButtons}>
          {hasArrows && (
            <>
              <button
                type="button"
                className={styles.arrowButton}
                onClick={e => handleArrowClick(e, 'left')}
                aria-label="Scroll to the left"
              >
                <Icon type="chevron" className={styles.icon} />
              </button>
              <button
                type="button"
                className={styles.arrowButton}
                onClick={e => handleArrowClick(e, 'right')}
                aria-label="Scroll to the right"
              >
                <Icon type="chevron" className={styles.icon} />
              </button>
            </>
          )}
        </span>
      </div>
      <div className={classNames(styles.slider)} ref={carouselRef}>
        <div
          className={styles.slidesContainer}
          id={contentId}
          role={contentRole}
          style={{
            '--slidesPerViewDesktop': slidesPerViewDesktop,
            '--slidesPerViewMobile': slidesPerViewMobile,
            '--childrenLength': children.length,
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
};
export default Slider;
